// UserFeedGridCategory.jsx
import React from 'react';
import { useLoaderData } from 'react-router';

import MetaTagsHelmet from '../component/MetaTagsHelmet.jsx';
import UniversalComponent from '../component/UniversalComponent.jsx';
import { EMPTY_ARRAY } from '../resource/defaults.js';
import { createInitialStoreWithRequest } from '../createInitialStore.js';
import getMetaTagsForHelmet from '../resource/getMetaTagsForHelmet.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import { BRANDING_NAME } from '../RemoteConfigKeys.js';
import { getI18nInstance } from '../resource/i18n.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { getFeedCategory } from '../resource/feedUtils.js';
import getTranslationKey from '../resource/getTranslationKey.js';
import getHttpProxyHeaders from '../resource/getHttpProxyHeaders.js';
import { fetchFeedTotalCount } from '../resource/fetchFeedTotalCount.js';

/**
 * React Router loader for UserFeedGridCategory
 * Uses lightweight HEAD request to check feed count for noindex determination
 */
export const loader = async ({ params, request }) => {
  const { store } = await createInitialStoreWithRequest({ params, request });
  const state = store.getState();

  const brandingName = getRemoteConfigData(state, BRANDING_NAME) || 'SWAG';

  // Get i18n instance for translations
  const { i18n } = getI18nInstance();
  const t = i18n.getFixedT(null, TranslationNamespace.SEO);

  // Parse URL to get search params
  const url = new URL(request.url);
  const category = getFeedCategory({
    category: params.category,
    search: url.search,
  });

  // Get HTTP proxy headers for API calls
  const httpProxyHeaders = getHttpProxyHeaders({ request });

  // Use lightweight HEAD request to check if feed has content
  // This is more efficient than fetching full feed data
  const feedCount = await fetchFeedTotalCount({
    feedNameWithQuery: category,
    httpProxyHeaders,
  });

  // Get title for meta tags
  const titleI18nId = getTranslationKey({ key: category });
  const feedTitle = t(titleI18nId, { ns: TranslationNamespace.FEED });
  const defaultTitle = t('seo-home-title');
  const title = `${feedTitle} - ${defaultTitle}`;

  // If no content found, add noindex (matching Next.js: if (userIds.length === 0) return getNoIndexMetadata())
  const robots = feedCount === 0 ? ['noindex', 'nofollow'] : [];

  const meta = getMetaTagsForHelmet({
    title,
    siteName: brandingName,
    hostname: request?.headers?.get('host') || 'swag.live',
    robots,
  });

  return {
    meta,
    staticContext: {
      requestHeaders: {
        hostname: request?.headers?.get('host') || 'swag.live',
      },
    },
  };
};

const UserFeedGridCategoryRoute = () => {
  const { meta = EMPTY_ARRAY } = useLoaderData();

  return (
    <>
      <MetaTagsHelmet metaTags={meta} />
      <UniversalComponent
        filename="Container_UserFeedGridList"
        importer={
          /* #__LOADABLE__ */ () => import('../container/UserFeedGridList.js')
        }
      />
    </>
  );
};

export default UserFeedGridCategoryRoute;
